import { keepPreviousData, queryOptions, useMutation } from '@tanstack/react-query'
import { API_BASE_URL, apiFetch, AuthError } from '@/api/client'
import type { components } from '@/api/schema'
import { useAuth } from '@/hooks/use-auth'
import { buildSearchParams, type CatalogSearchResponse, type DiscoveryRequest } from './storefront-api'
import type { DiscoveryState } from './search-state'

// Smart search (USA-1116): staff-only endpoints, because every decision is a paid model call.
export type SmartSearchDecision = components['schemas']['SmartSearchDecision']
export type SmartSearchApplied = components['schemas']['SmartSearchApplied']

type GetToken = () => Promise<string | null | undefined>

async function smartFetch<T>(
  getToken: GetToken,
  path: string,
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<T> {
  const token = await getToken()
  if (!token) throw new AuthError(401, 'Failed to acquire access token')
  const response = await apiFetch(`${API_BASE_URL}${path}?${params.toString()}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    signal,
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: unknown } | null
    throw new Error(
      typeof body?.detail === 'string' ? body.detail : `Request failed with HTTP ${response.status}.`,
    )
  }
  return (await response.json()) as T
}

export function useSmartSearchDecision() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: (query: string) =>
      smartFetch<SmartSearchDecision>(
        getAccessToken,
        '/shopify/smart-search/filters',
        new URLSearchParams({ q: query }),
      ),
  })
}

export function smartSearchQueryOptions(getToken: GetToken, request: DiscoveryRequest) {
  const params = buildSearchParams(request)
  return queryOptions({
    queryKey: ['storefront', 'smart-search', params.toString()],
    queryFn: ({ signal }) =>
      smartFetch<CatalogSearchResponse>(getToken, '/shopify/smart-search/search', params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

/** The page state a decision describes: a fresh search of its words, filters, ranges and order. */
export function stateFromDecision(decision: SmartSearchDecision): DiscoveryState {
  const rangeSelections: DiscoveryState['rangeSelections'] = {}
  Object.entries(decision.ranges ?? {}).forEach(([key, range]) => {
    const min = range.min ?? undefined
    const max = range.max ?? undefined
    if (min || max) rangeSelections[key] = { ...(min ? { min } : {}), ...(max ? { max } : {}) }
  })
  return {
    query: decision.search_query,
    page: 1,
    sort: decision.sort ?? 'featured',
    multiSelections: decision.filters ?? {},
    rangeSelections,
    ai: decision.query,
    ...(decision.rank
      ? {
          rank: {
            by: decision.rank.by,
            direction: decision.rank.direction,
            label: decision.rank.label,
          },
        }
      : {}),
  }
}

/**
 * Chip names for the values a decision filtered on, by filter key and value, for when the current
 * results no longer list those filters (an empty result has none). Filters read "Style: Round".
 */
export function appliedFilterNames(decision: SmartSearchDecision) {
  const names: Record<string, Record<string, string>> = {}
  decision.applied.forEach((item) => {
    if (item.kind !== 'category' && item.kind !== 'manufacturer' && item.kind !== 'filter') return
    const values = decision.filters?.[item.key] ?? []
    // A filter that picked several values names them "Round or Square".
    const labels = values.length === 1 ? [item.value] : item.value.split(' or ')
    if (!values.length || labels.length !== values.length) return
    names[item.key] = Object.fromEntries(
      values.map((value, index) => [
        value,
        item.kind === 'filter' ? `${item.label}: ${labels[index]}` : (labels[index] ?? value),
      ]),
    )
  })
  return names
}

/** Whether an applied item is still part of the page state (staff may have removed it). */
export function isStillApplied(item: SmartSearchApplied, state: DiscoveryState) {
  switch (item.kind) {
    case 'text':
      return Boolean(state.query)
    case 'sort':
      return state.sort !== 'featured'
    case 'rank':
      return Boolean(state.rank)
    case 'range':
      return Boolean(state.rangeSelections[item.key] || state.multiSelections[item.key]?.length)
    default:
      return Boolean(state.multiSelections[item.key]?.length)
  }
}

/** The page state without one applied item. */
export function withoutApplied(item: SmartSearchApplied, state: DiscoveryState): DiscoveryState {
  const multiSelections = { ...state.multiSelections }
  const rangeSelections = { ...state.rangeSelections }
  switch (item.kind) {
    case 'text':
      return { ...state, page: 1, query: '' }
    case 'sort':
      return { ...state, page: 1, sort: 'featured' }
    case 'rank':
      return { ...state, page: 1, rank: undefined }
    default:
      delete multiSelections[item.key]
      delete rangeSelections[item.key]
      return { ...state, page: 1, multiSelections, rangeSelections }
  }
}
